import { useEffect, useState } from "react";
import { api, API_BASE, formatMoney, formatDate, todayISO, openDownload } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, Check, ArrowDownLeft, ArrowUpRight, AlertCircle, Mail, FileDown } from "lucide-react";

const STATUS = {
  portfolio: { label: "Portföyde", color: "bg-blue-100 text-blue-700" },
  collected: { label: "Tahsil Edildi", color: "bg-emerald-100 text-emerald-700" },
  paid: { label: "Ödendi", color: "bg-emerald-100 text-emerald-700" },
  endorsed: { label: "Ciro Edildi", color: "bg-amber-100 text-amber-700" },
  bounced: { label: "Karşılıksız", color: "bg-rose-100 text-rose-700" },
};

export default function Cheques() {
  const [items, setItems] = useState([]);
  const [cari, setCari] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    cheque_number: "", type: "received", bank_name: "", branch: "",
    amount: "", currency: "TRY", cari_id: "",
    issue_date: todayISO(), due_date: todayISO(), status: "portfolio", notes: "",
  });

  const load = async () => {
    const [c, cr] = await Promise.all([api.get("/cheques"), api.get("/cari")]);
    setItems(c.data); setCari(cr.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/cheques", { ...form, amount: Number(form.amount) });
      toast.success("Çek eklendi");
      setOpen(false);
      setForm({ ...form, cheque_number: "", bank_name: "", amount: "", notes: "" });
      load();
    } catch { toast.error("Kayıt başarısız"); }
  };
  const setStatus = async (id, status) => { await api.put(`/cheques/${id}`, { status }); toast.success("Güncellendi"); load(); };
  const del = async (id) => { if (!window.confirm("Silinsin mi?")) return; await api.delete(`/cheques/${id}`); load(); };

  const filtered = filter === "all" ? items : items.filter(i => i.type === filter);
  const received = items.filter(i => i.type === "received" && i.status === "portfolio").reduce((s, i) => s + i.amount, 0);
  const issued = items.filter(i => i.type === "issued" && i.status === "portfolio").reduce((s, i) => s + i.amount, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-bold">Kıymetli Evrak</div>
          <h1 className="font-display text-4xl font-extrabold text-slate-900 dark:text-slate-100 mt-1">Çek Takibi</h1>
        </div>
        <div className="flex gap-2">
          <button data-testid="chq-reminder" onClick={async () => { try { const { data } = await api.post("/cheques/send-reminder"); if (data.status === "success") toast.success(`${data.count} çek için hatırlatma gönderildi`); else toast(data.reason || "Gönderilmedi"); } catch { toast.error("Gönderim başarısız"); } }} className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white border border-slate-200 hover:border-amber-500 text-slate-700 hover:text-amber-600 font-bold transition">
            <Mail className="w-4 h-4" /> Hatırlatma Gönder
          </button>
          <button data-testid="chq-xlsx" onClick={() => openDownload(`/api/export/cheques.xlsx`)} className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white border border-slate-200 hover:border-emerald-500 text-slate-700 hover:text-emerald-600 font-bold transition">
            <FileDown className="w-4 h-4" /> Excel
          </button>
          <button data-testid="add-cheque" onClick={() => setOpen(true)} className="flex items-center gap-2 px-5 py-3 rounded-xl bg-[#1E2A9C] text-white font-bold hover:bg-[#121A68] shadow-lg shadow-[#1E2A9C]/20"><Plus className="w-4 h-4" /> Yeni Çek</button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-5">
        <div className="p-6 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-widest text-emerald-600 font-bold">Alınan Çekler (Portföy)</div>
            <ArrowDownLeft className="w-4 h-4 text-emerald-500" />
          </div>
          <div className="font-mono-num text-3xl font-extrabold text-emerald-600 mt-3">{formatMoney(received, "TRY")}</div>
        </div>
        <div className="p-6 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-widest text-rose-600 font-bold">Verilen Çekler (Bekleyen)</div>
            <ArrowUpRight className="w-4 h-4 text-rose-500" />
          </div>
          <div className="font-mono-num text-3xl font-extrabold text-rose-600 mt-3">{formatMoney(issued, "TRY")}</div>
        </div>
      </div>

      <div className="flex gap-2">
        {[{k:"all",l:"Tümü"},{k:"received",l:"Alınan"},{k:"issued",l:"Verilen"}].map(t => (
          <button key={t.k} data-testid={`chq-filter-${t.k}`} onClick={() => setFilter(t.k)} className={`px-4 py-2 rounded-full text-sm font-bold transition ${filter === t.k ? "bg-[#1E2A9C] text-white" : "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50"}`}>{t.l}</button>
        ))}
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 uppercase text-[10px] tracking-widest">
            <tr>
              <th className="text-left px-6 py-3 font-bold">Tip</th>
              <th className="text-left px-6 py-3 font-bold">Çek No</th>
              <th className="text-left px-6 py-3 font-bold">Banka</th>
              <th className="text-left px-6 py-3 font-bold">Cari</th>
              <th className="text-left px-6 py-3 font-bold">Keşide</th>
              <th className="text-left px-6 py-3 font-bold">Vade</th>
              <th className="text-left px-6 py-3 font-bold">Durum</th>
              <th className="text-right px-6 py-3 font-bold">Tutar</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={9} className="text-center py-16 text-slate-400">Çek yok</td></tr>}
            {filtered.map(c => (
              <tr key={c.id} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50/50 dark:hover:bg-slate-800/50">
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold ${c.type === "received" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                    {c.type === "received" ? <><ArrowDownLeft className="w-3 h-3" /> Alınan</> : <><ArrowUpRight className="w-3 h-3" /> Verilen</>}
                  </span>
                </td>
                <td className="px-6 py-4 font-mono-num font-bold text-slate-800 dark:text-slate-200">{c.cheque_number}</td>
                <td className="px-6 py-4 text-slate-700 dark:text-slate-300">{c.bank_name}{c.branch && ` / ${c.branch}`}</td>
                <td className="px-6 py-4 text-slate-600 dark:text-slate-400">{cari.find(x => x.id === c.cari_id)?.name || "-"}</td>
                <td className="px-6 py-4 font-mono-num text-slate-600">{formatDate(c.issue_date)}</td>
                <td className="px-6 py-4 font-mono-num text-slate-700 dark:text-slate-300">{formatDate(c.due_date)}</td>
                <td className="px-6 py-4">
                  <select data-testid={`chq-status-${c.id}`} value={c.status} onChange={(e) => setStatus(c.id, e.target.value)} className={`px-2.5 py-1 rounded-md text-xs font-bold border-0 ${STATUS[c.status]?.color || "bg-slate-100 text-slate-700"}`}>
                    {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </td>
                <td className={`px-6 py-4 text-right font-mono-num font-bold ${c.type === "received" ? "text-emerald-600" : "text-rose-600"}`}>{formatMoney(c.amount, c.currency)}</td>
                <td className="px-6 py-4 text-right"><button data-testid={`del-chq-${c.id}`} onClick={() => del(c.id)} className="text-slate-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 grid place-items-center p-4">
          <form onSubmit={submit} className="bg-white rounded-2xl w-full max-w-xl p-8 relative">
            <button type="button" onClick={() => setOpen(false)} className="absolute top-4 right-4"><X className="w-5 h-5" /></button>
            <div className="font-display text-2xl font-extrabold mb-6">Yeni Çek</div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Tip</label>
                <select data-testid="chq-type" value={form.type} onChange={(e) => setForm({...form, type: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg">
                  <option value="received">Alınan (Müşteriden)</option>
                  <option value="issued">Verilen (Tedarikçiye)</option>
                </select></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Çek No</label>
                <input required data-testid="chq-number" value={form.cheque_number} onChange={(e) => setForm({...form, cheque_number: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg font-mono-num" /></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Banka</label>
                <input required data-testid="chq-bank" value={form.bank_name} onChange={(e) => setForm({...form, bank_name: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg" /></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Şube</label>
                <input data-testid="chq-branch" value={form.branch} onChange={(e) => setForm({...form, branch: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg" /></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Tutar</label>
                <input required type="number" step="0.01" data-testid="chq-amount" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg font-mono-num" /></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Para Birimi</label>
                <select data-testid="chq-currency" value={form.currency} onChange={(e) => setForm({...form, currency: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg">
                  {["TRY","USD","EUR"].map(c => <option key={c} value={c}>{c}</option>)}
                </select></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Keşide Tarihi</label>
                <input type="date" data-testid="chq-issue" value={form.issue_date} onChange={(e) => setForm({...form, issue_date: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg" /></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Vade</label>
                <input type="date" data-testid="chq-due" value={form.due_date} onChange={(e) => setForm({...form, due_date: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg" /></div>
              <div className="col-span-2"><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Cari</label>
                <select data-testid="chq-cari" value={form.cari_id} onChange={(e) => setForm({...form, cari_id: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg">
                  <option value="">-</option>
                  {cari.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select></div>
              <div className="col-span-2"><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Notlar</label>
                <textarea data-testid="chq-notes" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows="2" className="w-full mt-1 px-3 py-2 border rounded-lg"></textarea></div>
            </div>
            <button data-testid="chq-submit" className="w-full mt-6 py-3 bg-[#1E2A9C] text-white rounded-lg font-bold">Kaydet</button>
          </form>
        </div>
      )}
    </div>
  );
}
